/* ═══════════════════════════════════════════════════════════════════════════
   THE ROTA, AND THE LAW IT HAS TO LIVE INSIDE

   A rota is not a calendar. A calendar records what happened; a rota commits
   a named person to be somewhere at a time, and arbeidsmiljøloven chapter 10
   has a good deal to say about when you may do that. So the rules are here,
   as pure functions with their paragraph numbers attached, rather than inside
   the screen that draws the grid.

   ── What this checks, and the paragraph it comes from ─────────────────────

     § 10-4 (1)   9 hours a day, 40 hours a week of alminnelig arbeidstid
     § 10-6 (4)   overtime beyond that: 10 hours in 7 days, 25 in 4 weeks
     § 10-6 (8)   13 hours in a day and 48 in a week, all told
     § 10-8 (1)   11 hours' continuous rest in every 24
     § 10-8 (2)   35 hours' continuous rest in every 7 days
     § 10-8 (4)   every other Sunday and holiday off
     § 10-9 (1)   a break past 5½ hours; 30 minutes in total past 8
     § 10-11(1)   21:00 to 06:00 is night, and night work needs a reason

   ── What it cannot check, and says so ─────────────────────────────────────

   Nearly every one of those limits can be moved, and moved lawfully:

     a tariffavtale can set different ones outright
     a written agreement with tillitsvalgte can cut the 11 hours to 8
       (§ 10-8 third paragraph) and can average the weekly hours (§ 10-5)
     Arbeidstilsynet can give consent for more (§ 10-6 sixth paragraph)
     an employee can agree in writing to more overtime (§ 10-6 fourth)
     under-18s have an entirely different chapter (§ 11-2)

   None of that is knowable from a shift's start and end time. So this module
   reports what it checked against the default limits and names the paragraph,
   and every screen that shows a finding says it is a check and not a legal
   opinion. A tool that told a manager a lawful rota was illegal would get
   switched off within a week, and a tool that told them an unlawful one was
   fine would be worse — so it claims neither, and says which.

   `limits()` takes the business's own agreed figures so that a workplace with
   a tariffavtale checks against its own numbers rather than against the
   statutory defaults it has lawfully departed from.
   ═══════════════════════════════════════════════════════════════════════ */
import { toMinutes, fromMinutes, weekdayOf, hoursOn, openingHours } from './time.js';

/** The statutory defaults. Every one of them is a starting point that an
    agreement may lawfully move — which is why they are arguments and not
    constants scattered through the checks. */
export const DEFAULTS = {
  dayHours: 9,          // § 10-4 (1)
  weekHours: 40,        // § 10-4 (1)
  dayTotal: 13,         // § 10-6 (8)  including overtime
  weekTotal: 48,        // § 10-6 (8)  averaged over 8 weeks
  restDay: 11,          // § 10-8 (1)
  restWeek: 35,         // § 10-8 (2)
  breakAfter: 5.5,      // § 10-9 (1)
  breakMin: 30,         // minutes, when the day is 8 hours or more
  nightFrom: 21 * 60,   // § 10-11 (1)
  nightTo: 6 * 60,
  sundaysOffIn: 2,      // § 10-8 (4) — free every other Sunday
};

/** The limits actually in force here: the statutory defaults with whatever
    the workplace has lawfully agreed on top. */
export const limits = (agreed = {}) => ({ ...DEFAULTS, ...agreed });

/* ── A shift ──────────────────────────────────────────────────────────────
   `{ id, staffId, date: 'YYYY-MM-DD', from: 'HH:MM', to: 'HH:MM', breakMin }`
   A shift that ends before it starts runs past midnight and is not a
   mistake — a restaurant closing at 01:00 is the ordinary case. */

export function minutesOf(shift) {
  const a = toMinutes(shift.from);
  const b = toMinutes(shift.to);
  const span = b > a ? b - a : b + 24 * 60 - a;
  return Math.max(0, span - (shift.breakMin || 0));
}

export const hoursOf = shift => minutesOf(shift) / 60;

/** Absolute minutes from an epoch day, so two shifts on different dates can
    be compared without date arithmetic in every caller. */
function span(shift) {
  const day = Date.parse(shift.date + 'T00:00:00Z') / 60000;
  const a = day + toMinutes(shift.from);
  const b = day + toMinutes(shift.to);
  return [a, b > a ? b : b + 24 * 60];
}

/** How much of a shift falls in the night window, in minutes. § 10-11 (1) */
export function nightMinutes(shift, l = DEFAULTS) {
  const [a, b] = span(shift);
  let n = 0;
  for (let m = a; m < b; m++) {
    const clock = ((m % 1440) + 1440) % 1440;
    if (clock >= l.nightFrom || clock < l.nightTo) n++;
  }
  return n;
}

const isoWeek = dateISO => {
  const d = new Date(dateISO + 'T00:00:00Z');
  const day = (d.getUTCDay() + 6) % 7;          // Monday = 0
  d.setUTCDate(d.getUTCDate() - day);
  return d.toISOString().slice(0, 10);
};

/* ── The checks ───────────────────────────────────────────────────────────
   Each returns findings: `{ law, level, staffId, date, say }`.

   `level` is 'over' when a stated limit is passed and 'watch' when something
   is worth a human look but may be perfectly lawful. Nothing returns
   'unlawful', because this module cannot know that. */

const F = (law, level, staffId, date, say) => ({ law, level, staffId, date, say });

export function check(shifts, agreed = {}, people = []) {
  const l = limits(agreed);
  const out = [];
  const name = id => people.find(p => p.id === id)?.name || 'This person';

  /* Per person, in order, because every rest rule is about the gap between
     one shift and the next. */
  const byPerson = {};
  shifts.forEach(s => { (byPerson[s.staffId] ||= []).push(s); });

  Object.entries(byPerson).forEach(([staffId, list]) => {
    const mine = list.slice().sort((a, b) => span(a)[0] - span(b)[0]);
    const who = name(staffId);

    mine.forEach(s => {
      const h = hoursOf(s);

      /* § 10-4 (1) and § 10-6 (8) — the day. */
      if (h > l.dayTotal) {
        out.push(F('§ 10-6 (8)', 'over', staffId, s.date,
          `${who} is on for ${h.toFixed(1)} hours. Total working time in a day `
          + `may not pass ${l.dayTotal}, whatever has been agreed.`));
      } else if (h > l.dayHours) {
        out.push(F('§ 10-4 (1)', 'watch', staffId, s.date,
          `${who} is on for ${h.toFixed(1)} hours — past the ${l.dayHours} of `
          + 'alminnelig arbeidstid. The excess counts as overtime, which needs '
          + 'a reason under § 10-6.'));
      }

      /* § 10-9 (1) — the break. */
      const gross = minutesOf(s) + (s.breakMin || 0);
      if (gross > l.breakAfter * 60 && !(s.breakMin > 0)) {
        out.push(F('§ 10-9 (1)', 'over', staffId, s.date,
          `${who} works past ${l.breakAfter} hours with no break on the rota.`));
      } else if (gross >= 8 * 60 && (s.breakMin || 0) < l.breakMin) {
        out.push(F('§ 10-9 (1)', 'over', staffId, s.date,
          `A day of ${(gross / 60).toFixed(1)} hours needs at least `
          + `${l.breakMin} minutes of break in total; this one has `
          + `${s.breakMin || 0}.`));
      }

      /* § 10-11 (1) — night. */
      const night = nightMinutes(s, l);
      if (night > 0) {
        out.push(F('§ 10-11 (1)', 'watch', staffId, s.date,
          `${Math.round(night)} minutes of this shift fall between 21:00 and `
          + '06:00. Night work is only allowed where the nature of the work '
          + 'makes it necessary.'));
      }
    });

    /* § 10-8 (1) — 11 hours between shifts. */
    for (let i = 1; i < mine.length; i++) {
      const gap = (span(mine[i])[0] - span(mine[i - 1])[1]) / 60;
      if (gap < l.restDay) {
        out.push(F('§ 10-8 (1)', 'over', staffId, mine[i].date,
          `${who} has ${gap.toFixed(1)} hours off between the shift on `
          + `${mine[i - 1].date} and this one. The rule is ${l.restDay} — it `
          + 'can go down to 8 by written agreement with tillitsvalgte, and no '
          + 'lower.'));
      }
    }

    /* § 10-4 (1) and § 10-8 (2) — the week. */
    const weeks = {};
    mine.forEach(s => { (weeks[isoWeek(s.date)] ||= []).push(s); });

    Object.entries(weeks).forEach(([week, list2]) => {
      const h = list2.reduce((n, s) => n + hoursOf(s), 0);
      if (h > l.weekTotal) {
        out.push(F('§ 10-6 (8)', 'over', staffId, week,
          `${who} is rostered ${h.toFixed(1)} hours in the week of ${week}. `
          + `Total working time may not pass ${l.weekTotal} in a week.`));
      } else if (h > l.weekHours) {
        out.push(F('§ 10-4 (1)', 'watch', staffId, week,
          `${who} is rostered ${h.toFixed(1)} hours in the week of ${week}, `
          + `past the ${l.weekHours} of alminnelig arbeidstid.`));
      }

      /* The longest continuous break inside the week. */
      const sorted = list2.slice().sort((a, b) => span(a)[0] - span(b)[0]);
      const start = Date.parse(week + 'T00:00:00Z') / 60000;
      let best = 0;
      let last = start;
      sorted.forEach(s => {
        best = Math.max(best, span(s)[0] - last);
        last = Math.max(last, span(s)[1]);
      });
      best = Math.max(best, start + 7 * 1440 - last);
      if (best / 60 < l.restWeek) {
        out.push(F('§ 10-8 (2)', 'over', staffId, week,
          `${who}'s longest unbroken rest in the week of ${week} is `
          + `${(best / 60).toFixed(1)} hours. The rule is ${l.restWeek}.`));
      }
    });

    /* § 10-8 (4) — Sundays. Free every other one.
       `weekdayOf` counts from Monday, so Sunday is 6 and not 0. This read 0
       first time round, which meant the Sunday rule was quietly checking
       Mondays — a test caught it, which is the entire argument for having
       the rules in a module with tests rather than inside the grid. */
    const sundays = mine
      .filter(s => weekdayOf(s.date) === 6)
      .map(s => s.date)
      .sort();
    const runs = [];
    sundays.forEach(d => {
      const prev = runs[runs.length - 1];
      const week = 7 * 86400000;
      if (prev && Date.parse(d) - Date.parse(prev[prev.length - 1]) === week) prev.push(d);
      else runs.push([d]);
    });
    runs.filter(r => r.length >= l.sundaysOffIn).forEach(r => {
      out.push(F('§ 10-8 (4)', 'over', staffId, r[r.length - 1],
        `${who} is on ${r.length} Sundays in a row (${r.join(', ')}). `
        + 'The rule is free every other Sunday; working more in a row needs a '
        + 'written agreement, and never more than three.'));
    });
  });

  return out;
}

/** The findings, grouped for a screen that wants a count rather than a list. */
export function summary(findings) {
  return {
    over: findings.filter(f => f.level === 'over').length,
    watch: findings.filter(f => f.level === 'watch').length,
    laws: [...new Set(findings.map(f => f.law))].sort(),
  };
}

/* ── Generating one ───────────────────────────────────────────────────────

   Not an optimiser. A rota that a manager cannot predict is a rota they
   rewrite by hand, so this one is deliberately simple and explicable: cover
   the opening hours, spread the load evenly, rotate who takes the awkward
   days, and stop before breaking a rest rule rather than producing something
   that has to be argued with afterwards.

   It is a first draft. Every shift it makes can be moved, and the screen
   says so — a generated rota nobody may edit is a worse tool than a blank
   grid.
*/

/**
 * @param {object} o
 *   dates      ['YYYY-MM-DD', …] the days to cover
 *   people     [{id, name}]
 *   hoursLine  the business's opening-hours line
 *   cover      how many people on at once (default 1)
 *   maxDay     longest shift to hand out, in hours
 *   agreed     the workplace's own limits
 */
export function generate({ dates = [], people = [], hoursLine, cover = 1,
                           maxDay = 7.5, agreed = {} } = {}) {
  const l = limits(agreed);
  const out = [];
  if (!people.length) return out;

  /* Who has had how much, so the next shift goes to whoever has least. This
     is the whole of the fairness model and it is worth being honest that it
     is that simple: it spreads hours evenly and it does not know about
     anybody's preferences, children or Tuesday evening class. */
  const load = Object.fromEntries(people.map(p => [p.id, 0]));
  const lastEnd = {};
  let n = 0;

  dates.forEach(dateISO => {
    const open = hoursOn(dateISO, hoursLine);
    if (!open) return;                       // shut that day — nobody on

    const dayMin = open.close - open.open;
    /* A day longer than one person may work is split into consecutive
       shifts rather than handed to somebody as a 12-hour day. */
    const parts = Math.max(1, Math.ceil(dayMin / (maxDay * 60)));
    const part = Math.round(dayMin / parts / 5) * 5;

    for (let p = 0; p < parts; p++) {
      const from = open.open + p * part;
      const to = p === parts - 1 ? open.close : from + part;
      const gross = to - from;
      const brk = gross >= 8 * 60 ? l.breakMin : (gross > l.breakAfter * 60 ? 30 : 0);

      for (let c = 0; c < cover; c++) {
        /* Least-loaded first, and skip anybody the 11-hour rule rules out —
           producing a shift this module's own checker would flag is not a
           draft, it is a bug. */
        const start = Date.parse(dateISO + 'T00:00:00Z') / 60000 + from;
        const pick = people
          .slice()
          .sort((a, b) => load[a.id] - load[b.id])
          .find(person => {
            if (out.some(s => s.staffId === person.id && s.date === dateISO
                              && toMinutes(s.from) === from)) return false;
            const end = lastEnd[person.id];
            return end == null || (start - end) / 60 >= l.restDay;
          });
        if (!pick) continue;                 // nobody lawfully available

        out.push({
          id: 'G' + (++n),
          staffId: pick.id,
          date: dateISO,
          from: fromMinutes(from),
          to: fromMinutes(to),
          breakMin: brk,
          generated: true,
        });
        load[pick.id] += (gross - brk) / 60;
        lastEnd[pick.id] = Date.parse(dateISO + 'T00:00:00Z') / 60000 + to;
      }
    }
  });

  return out;
}

/** What the generator could not do, in words a manager can act on. An empty
    day on a rota with no explanation is the thing that gets a tool blamed. */
export function gaps({ dates = [], shifts = [], hoursLine, cover = 1 }) {
  const out = [];
  dates.forEach(dateISO => {
    const open = hoursOn(dateISO, hoursLine);
    if (!open) return;
    const on = shifts.filter(s => s.date === dateISO);
    if (!on.length) {
      out.push({ date: dateISO, say: 'Open, and nobody is on.' });
      return;
    }
    const thin = new Set();
    for (let m = open.open; m < open.close; m += 30) {
      const n = on.filter(s => toMinutes(s.from) <= m
        && (toMinutes(s.to) > m || toMinutes(s.to) < toMinutes(s.from))).length;
      if (n < cover) thin.add(fromMinutes(m));
    }
    if (thin.size) {
      const list = [...thin];
      out.push({
        date: dateISO,
        say: `Under ${cover} on from ${list[0]}`
           + (list.length > 1 ? ` and ${list.length - 1} other half-hours.` : '.'),
      });
    }
  });
  return out;
}

/** Total hours per person, for the summary strip and the payroll question
    that always follows. */
export function totals(shifts, people = []) {
  return people.map(p => ({
    ...p,
    hours: shifts.filter(s => s.staffId === p.id).reduce((n, s) => n + hoursOf(s), 0),
    shifts: shifts.filter(s => s.staffId === p.id).length,
  }));
}

export { openingHours };
